// English (switchable). Must mirror nl.ts exactly (same keys, EN values).
import type { nl } from "./nl";

export const en: Record<keyof typeof nl, string> = {
  "app.name": "Datow",
  "app.payoff": "Your data in motion",

  // Navigation
  "navgroup.beheer": "Management",
  "navgroup.producten": "Products",
  "navgroup.inzicht": "Insight",
  "nav.overzicht": "Overview",
  "nav.abonnementen": "Subscriptions",
  "nav.pakketten": "Packages",
  "nav.klanten": "Customers",
  "nav.leads": "Leads",
  "nav.datalift": "DataLift",
  "nav.databridge": "DataBridge",
  "nav.datadocs": "DataDocs",
  "nav.dataapps": "DataApps",
  "nav.datacloud": "DataCloud",
  "nav.rapportages": "Reports",
  "nav.auditlog": "Audit log",

  // Common actions
  "action.new": "New",
  "action.newAbonnement": "New subscription",
  "action.newPakket": "New package",
  "action.newKlant": "New customer",
  "action.edit": "Edit",
  "action.cancel": "Cancel",
  "action.save": "Save",
  "action.create": "Create",
  "action.search": "Search",
  "action.export": "Export",
  "action.verlengen": "Renew",
  "action.opzeggen": "Cancel subscription",
  "action.viewAll": "View all",
  "action.back": "Back",
  "action.copy": "Copy",

  // User menu
  "user.profile": "Profile",
  "user.preferences": "Preferences",
  "user.language": "Language",
  "user.shortcuts": "Shortcuts",
  "user.logout": "Sign out",

  // Status labels (7 states)
  "status.proef": "Trial",
  "status.inbehandeling": "In progress",
  "status.actief": "Active",
  "status.verlenging": "Up for renewal",
  "status.betaling": "Awaiting payment",
  "status.opgezegd": "Cancelled",
  "status.verlopen": "Expired",
  // Component statuses (7); trial/active/cancelled are shared with the subscription
  "status.besteld": "Ordered",
  "status.implementatie": "In implementation",
  "status.opgeleverd": "Delivered",
  "status.vervallen": "Ended",

  // Env banner (non-production)
  "banner.acc": "Acceptance environment",
  "banner.local": "Development environment",
  "banner.note": "data here is separate from production",

  // Login / gateway
  "login.title": "Sign in",
  "login.lede": "Welcome back to the Datow Backend.",
  "login.employees": "Employees",
  "login.microsoft": "Sign in with Microsoft",
  "login.ssoNote": "Single sign-on with your Datow account · MFA via Entra ID.",
  "login.or": "or",
  "login.email": "Email address",
  "login.password": "Password",
  "login.emailLogin": "Sign in with email",
  "login.emailSoon": "Email/password sign-in is coming soon.",
  "login.partner": "Partner portal",
  "login.partnerBadge": "Coming soon",
  "login.partnerBody": "Access for integration partners and resellers. Not available yet.",
  "login.partnerButton": "Sign in as partner",
  "login.help": "No account yet? Contact your administrator",
  "login.contactEmail": "info@datow.nl",
  "login.devTitle": "Developer login (mock)",
  "login.devEmail": "Email address",
  "login.devRoles": "Roles",
  "login.devLogin": "Sign in (mock)",

  // Overview (dashboard)
  "overzicht.title": "Overview",
  "overzicht.subtitle": "The current state of your subscription portfolio.",
  "overzicht.kpi.actief": "Active subscriptions",
  "overzicht.kpi.maandomzet": "Monthly revenue",
  "overzicht.kpi.proef": "Running trials",
  "overzicht.kpi.aandacht": "Needs attention",
  "overzicht.attention.title": "Needs attention",
  "overzicht.attention.empty": "Nothing needs attention — all running.",
  "overzicht.distribution.title": "Distribution by product group",
  "overzicht.kpi.wachtOpDatum": "Awaiting start date",
  "overzicht.recent.title": "Recent subscriptions",

  // Subscriptions
  "abonnementen.title": "Subscriptions",
  "abonnementen.view.alle": "All",
  "abonnementen.view.actief": "Active",
  "abonnementen.view.proef": "Trial",
  "abonnementen.view.verlenging": "Up for renewal",
  "abonnementen.view.betaling": "Awaiting payment",
  "abonnementen.search": "Search subscription or customer…",
  "abonnementen.col.abonnement": "Subscription",
  "abonnementen.col.pakket": "Components",
  "abonnementen.col.status": "Status",
  "abonnementen.col.startdatum": "Start date",
  "abonnementen.col.verlengtOp": "Renews on",
  "abonnementen.col.maandbedrag": "Monthly amount",
  "abonnementen.empty.title": "No subscriptions found",
  "abonnementen.empty.body": "Adjust your filters or create a new subscription.",
  "abonnementen.selected": "{{count}} selected",

  // Subscription — dossier
  "abonnement.tab.overzicht": "Overview",
  "abonnement.tab.contract": "Contract",
  "abonnement.tab.tijdlijn": "Timeline",
  "abonnement.tab.audit": "Audit log",
  "abonnement.section.samenvatting": "Summary",
  "abonnement.section.pakket": "Components",
  "abonnement.section.contract": "Contract",
  "abonnement.section.tijdlijn": "Timeline",
  "abonnement.section.partijen": "Parties",
  "abonnement.section.audit": "Audit log",
  "abonnement.field.klant": "Customer",
  "abonnement.field.pakket": "Components",
  "abonnement.field.jaarbedrag": "Yearly amount",
  "abonnement.field.opzegbaarTot": "Cancellable until",
  "abonnement.field.status": "Status",
  "abonnement.field.maandbedrag": "Monthly amount",
  "abonnement.field.startdatum": "Start date",
  "abonnement.field.verlengtOp": "Renews on",
  "abonnement.field.looptijd": "Term",
  "abonnement.field.opzegtermijn": "Notice period",
  "abonnement.field.facturatie": "Billing",
  "abonnement.field.betaalwijze": "Payment method",
  "abonnement.field.maanden": "{{count}} months",
  "abonnement.field.maanden_one": "{{count}} month",
  "abonnement.changeStatus": "Change status",
  "abonnement.moveTo": "Move to",

  // New subscription
  "abonnementNieuw.title": "New subscription",
  "abonnementNieuw.step.klant": "Customer",
  "abonnementNieuw.step.pakket": "Components",
  "abonnementNieuw.step.looptijd": "Term & start",
  "abonnementNieuw.step.facturatie": "Billing",
  "abonnementNieuw.summary": "Summary",
  "abonnementNieuw.summaryNote":
    "The subscription starts in the In progress status. Activate it once the integration is ready.",
  "abonnementNieuw.submit": "Create subscription",
  "abonnementNieuw.alsProef": "Create as a trial — € 0, not an order yet",
  "abonnementNieuw.geenOnderdelen": "Pick at least one component from the price list.",

  // Price list (the catalogue)
  "pakketten.title": "Price list",
  "pakketten.subtitle": "The sellable components: product group, model and tier, each with one list price.",
  "pakketten.perMaand": "per month",
  "pakketten.perJaar": "per year",
  "pakketten.search": "Search by product code, model or name…",
  "pakketten.col.productcode": "Product code",
  "pakketten.col.pakket": "Component",
  "pakketten.col.model": "Model",
  "pakketten.col.niveau": "Tier",
  "pakketten.col.maandtarief": "List price",
  "pakketten.col.jaartarief": "Per year",
  "pakketten.col.inGebruik": "In use",
  "pakketten.col.status": "Status",
  "pakketten.filter.groep": "Product group",
  "pakketten.filter.niveau": "Tier",
  "pakketten.filter.alle": "All",
  "pakketten.status.actief": "Active",
  "pakketten.status.concept": "Draft",
  "pakketten.status.uitgefaseerd": "Phased out",
  "pakketten.aantalInGebruik": "{{count}} in use",
  "pakketten.empty.title": "No catalogue entries found",
  "pakketten.empty.body": "Adjust your filters or search for another product code.",
  "pakketten.grootboek": "Ledger",
  "pakketten.grootboekLeeg": "not set yet",
  "pakketten.jaarNote": "The yearly amount is 12 × the monthly amount — no prepayment discount.",
  "pakketten.uitgefaseerdNote":
    "Phased-out codes stay in place so older components and invoices remain traceable, but they can no longer be selected.",

  // Components (the lines on a subscription)
  "onderdelen.title": "Components",
  "onderdelen.subtitle": "The packages within this subscription, each with its own status and start date.",
  "onderdelen.col.onderdeel": "Component",
  "onderdelen.col.productcode": "Product code",
  "onderdelen.col.status": "Status",
  "onderdelen.col.bedrag": "Agreed",
  "onderdelen.col.standaard": "List price",
  "onderdelen.col.afwijking": "Deviation",
  "onderdelen.col.ingangsdatum": "Start date",
  "onderdelen.col.eerstePeriode": "First period",
  "onderdelen.opStandaard": "at list price",
  "onderdelen.geenDatum": "not known yet",
  "onderdelen.wachtOpDatum": "Awaiting start date",
  "onderdelen.wachtOpDatumBody": "Sold, but without a start date — and therefore not yet invoiced.",
  "onderdelen.proRataNote": "{{dagen}} of {{totaal}} days until the renewal date",
  "onderdelen.volledigePeriode": "full period",
  "onderdelen.opgevolgdDoor": "Succeeded by a higher tier",
  "onderdelen.vervangt": "Replaces an earlier component",
  "onderdelen.empty.title": "No components yet",
  "onderdelen.empty.body": "Add a component from the price list to this subscription.",
  "onderdelen.add": "Add component",
  "onderdelen.totaal": "Total per month",

  // Leads (opportunities) & call-back list
  "leads.title": "Leads",
  "leads.subtitle": "Opportunities with an expected amount and period, plus who needs a call back.",
  "leads.new": "New lead",
  "leads.terugbellijst": "To call back",
  "leads.periode.week": "This week",
  "leads.periode.maand": "This month",
  "leads.periode.kwartaal": "This quarter",
  "leads.achterstallig": "Overdue — the date has passed",
  "leads.afvinken": "Mark done",
  "leads.geenActies": "Nobody to call back",
  "leads.geenActiesBody": "There are no open follow-ups in this period.",
  "leads.geenForecast": "No open leads yet to build a forecast from.",
  "leads.status.open": "Open",
  "leads.status.gewonnen": "Won",
  "leads.status.verloren": "Lost",
  "leads.col.kwalificatie": "A/B/C",
  "leads.col.omschrijving": "Description",
  "leads.col.verwachtePeriode": "Expected",
  "leads.col.verwachtBedrag": "Expected amount",
  "leads.col.status": "Status",
  "leads.col.notities": "Notes",
  "leads.col.terugbellenOp": "Call back on",
  "leads.col.actie": "Action",
  "leads.empty.title": "No leads found",
  "leads.empty.body": "Adjust your filters or record a new opportunity.",
  "leads.forecastNote":
    "The expected revenue is unweighted: A/B/C is a qualification, not a probability. A weight per class is still a choice.",

  // Contact moments (call notes & follow-ups)
  "momenten.title": "Call notes",
  "momenten.notitie": "Note",
  "momenten.vervolgactie": "Follow-up",
  "momenten.geen": "No notes recorded yet.",
  "momenten.terugbellenOp": "Call back on {{datum}}",
  "momenten.afgerond": "Done",
  "momenten.add": "Add note",

  // Customer file
  "klant.dossier": "Customer file",
  "klant.section.contactpersonen": "Contacts",
  "klant.section.abonnementen": "Subscriptions",
  "klant.section.tijdlijn": "Timeline",
  "klant.section.gegevens": "Details",
  "klant.field.kvk": "Chamber of Commerce",
  "klant.field.plaats": "City",
  "klant.field.sinds": "Customer since",
  "klant.field.type": "Type",
  "klant.contact.primair": "primary",
  "klant.contact.facturatie": "invoicing",
  "klant.contact.ondertekenaar": "signatory",
  "klant.contact.geen": "No contacts recorded yet.",
  "klant.geenAbonnementen": "This customer has no subscription yet.",

  // Customers
  "klanten.title": "Customers",
  "klanten.view.alle": "All",
  "klanten.view.klant": "Customer",
  "klanten.view.prospect": "Prospect",
  "klanten.search": "Search customer, city or CoC…",
  "klanten.col.klant": "Customer",
  "klanten.col.kvk": "CoC",
  "klanten.col.contact": "Contact",
  "klanten.col.contacten": "Contacts",
  "klanten.col.abonnementen": "Subscriptions",
  "klanten.col.sinds": "Customer since",
  "klanten.col.type": "Type",
  "klanten.type.klant": "Customer",
  "klanten.type.prospect": "Prospect",
  "klanten.contactUnassigned": "— to assign",

  // Common
  "common.loading": "Loading…",
  "common.results": "{{count}} results",
  "common.results_one": "{{count}} result",
  "common.page": "Page {{page}}",
  "common.of": "of",
  "common.previous": "Previous",
  "common.next": "Next",
  "common.error": "Something went wrong",
  "common.retry": "Try again",
  "common.none": "—",
};
